import React, { useState, useEffect } from "react";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import band_3 from "../../../images/3-band.webp";
import band_4 from "../../../images/4-band.webp";
import band_5 from "../../../images/5-band.webp";
import band_6 from "../../../images/6-band.webp";
import SharedHelmet from "../Helmet";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import ParallelResistorCalculator from "./ParallelResistorCalculator";
import SeriesResistorCalculator from "./ResistorsInSeriesCalculator";
import ResistanceCalculator from "./CundoctorResistanceCalculator";
import { isMobile } from "react-device-detect";

const TITLE = "حاسبة المقاومة - احسب قيمة المقاومة بسهولة";
const DESCRIPTION =
  "تساعدك هذه الحاسبة على معرفة قيمة المقاومة بناءً على ألوانها، وحساب مقاومة مجموعة من المقاومات المتصلة على التوالي أو بالتوازي، وكذلك مقاومة الموصل بناءً على حجمه وموصليته.";
const KEYWORDS =
  "حاسبة المقاومة, حاسبة الألوان, مقاومات كهربائية, حساب قيمة المقاومة, مقاومة المقاومات بالتوازي, مقاومة المقاومات على التوالي, حساب مقاومة الموصل";
const OG_URL = "https://do-calculate.com/calculator/ar/resistor-calculator";

const colorCodes = {
  black:  { value: 0, multiplier: 1,          tolerance: null,     tempCoeff: "250ppm/K", colorAr: "أسود",    hex: "#1a1a1a", textColor: "#fff" },
  brown:  { value: 1, multiplier: 10,         tolerance: "±1%",    tempCoeff: "100ppm/K", colorAr: "بني",     hex: "#795548", textColor: "#fff" },
  red:    { value: 2, multiplier: 100,        tolerance: "±2%",    tempCoeff: "50ppm/K",  colorAr: "أحمر",    hex: "#e53935", textColor: "#fff" },
  orange: { value: 3, multiplier: 1000,       tolerance: "±0.05%", tempCoeff: "15ppm/K",  colorAr: "برتقالي", hex: "#fb8c00", textColor: "#fff" },
  yellow: { value: 4, multiplier: 10000,      tolerance: "±0.02%", tempCoeff: "25ppm/K",  colorAr: "أصفر",    hex: "#fdd835", textColor: "#333" },
  green:  { value: 5, multiplier: 100000,     tolerance: "±0.5%",  tempCoeff: "20ppm/K",  colorAr: "أخضر",    hex: "#43a047", textColor: "#fff" },
  blue:   { value: 6, multiplier: 1000000,    tolerance: "±0.25%", tempCoeff: "10ppm/K",  colorAr: "أزرق",    hex: "#1e88e5", textColor: "#fff" },
  violet: { value: 7, multiplier: 10000000,   tolerance: "±0.1%",  tempCoeff: "5ppm/K",   colorAr: "بنفسج",   hex: "#8e24aa", textColor: "#fff" },
  grey:   { value: 8, multiplier: 100000000,  tolerance: "±0.01%", tempCoeff: "1ppm/K",   colorAr: "رمادي",   hex: "#757575", textColor: "#fff" },
  white:  { value: 9, multiplier: 1000000000, tolerance: null,     tempCoeff: null,        colorAr: "أبيض",    hex: "#f5f5f5", textColor: "#333" },
  gold:   { value: null, multiplier: 0.1,    tolerance: "±5%",    tempCoeff: null,        colorAr: "ذهبي",    hex: "#d4a017", textColor: "#333" },
  silver: { value: null, multiplier: 0.01,   tolerance: "±10%",   tempCoeff: null,        colorAr: "فضي",     hex: "#aaaaaa", textColor: "#333" },
};

// Format resistance with proper units
function formatResistance(ohms) {
  if (ohms === null || ohms === undefined || isNaN(ohms)) return { value: "خطأ", unit: "" };
  if (ohms >= 1_000_000) return { value: (ohms / 1_000_000).toLocaleString("en-US", { maximumFractionDigits: 2 }), unit: "MΩ" };
  if (ohms >= 1_000)     return { value: (ohms / 1_000).toLocaleString("en-US",     { maximumFractionDigits: 2 }), unit: "kΩ" };
  return { value: ohms.toLocaleString("en-US", { maximumFractionDigits: 2 }), unit: "Ω" };
}

// Live SVG resistor — bands update as colors are picked
function ResistorSVG({ numBands, bandColors, bands }) {
  const visibleBands = bands.filter((b) => b.show);

  const bodyWidth  = 160;
  const bodyHeight = 40;
  const bodyX      = 65;
  const bodyY      = 30;
  const svgWidth   = 290;
  const svgHeight  = 100;
  const wireY      = bodyY + bodyHeight / 2;

  const bandWidth  = 12;
  const padding    = 16;
  const usableWidth = bodyWidth - padding * 2;
  const count = visibleBands.length;
  const bandSpacing = count > 1 ? usableWidth / (count - 1) : 0;

  return (
    <svg
      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      style={{ width: "100%", maxWidth: "340px", margin: "0 auto", display: "block" }}
      aria-label="resistor preview"
    >
      {/* Wires */}
      <line x1="0"                  y1={wireY} x2={bodyX}              y2={wireY} stroke="#999" strokeWidth="3" strokeLinecap="round" />
      <line x1={bodyX + bodyWidth}  y1={wireY} x2={svgWidth}           y2={wireY} stroke="#999" strokeWidth="3" strokeLinecap="round" />

      {/* Body shadow */}
      <rect x={bodyX + 3} y={bodyY + 3} width={bodyWidth} height={bodyHeight} rx="9" fill="rgba(0,0,0,0.12)" />
      {/* Body fill */}
      <rect x={bodyX} y={bodyY} width={bodyWidth} height={bodyHeight} rx="9" fill="#d4c89a" />

      {/* Color bands */}
      {visibleBands.map((band, i) => {
        const hex = colorCodes[bandColors[band.key]]?.hex || "#ccc";
        const x   = bodyX + padding + (count === 1 ? 0 : i * bandSpacing) - bandWidth / 2;
        // clip band to body bounds
        const clippedX = Math.max(bodyX, Math.min(x, bodyX + bodyWidth - bandWidth));
        return (
          <rect
            key={band.key}
            x={clippedX}
            y={bodyY}
            width={bandWidth}
            height={bodyHeight}
            fill={hex}
            style={{ transition: "fill 0.2s ease" }}
          />
        );
      })}

      {/* Body border on top */}
      <rect x={bodyX} y={bodyY} width={bodyWidth} height={bodyHeight} rx="9" fill="none" stroke="#b5a87a" strokeWidth="1.5" />

      {/* Terminal dots */}
      <circle cx={bodyX}             cy={wireY} r="4" fill="#888" />
      <circle cx={bodyX + bodyWidth} cy={wireY} r="4" fill="#888" />
    </svg>
  );
}

// Swatch color picker for one band
function SwatchPicker({ label, bandKey, selected, onChange, allowedColors }) {
  const colors = allowedColors
    ? Object.keys(colorCodes).filter((c) => allowedColors.includes(c))
    : Object.keys(colorCodes);

  return (
    <div style={{ marginBottom: "18px", paddingBottom: "18px", borderBottom: "1px solid #f0f0f0" }}>
      <div style={{ fontWeight: 700, fontSize: "13px", color: "#333", marginBottom: "10px" }}>
        {label}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "8px" }}>
        {colors.map((color) => {
          const isSelected = selected === color;
          return (
            <button
              key={color}
              title={colorCodes[color].colorAr}
              onClick={() => onChange(bandKey, color)}
              aria-label={colorCodes[color].colorAr}
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                border: "none",
                cursor: "pointer",
                backgroundColor: colorCodes[color].hex,
                boxShadow: isSelected
                  ? `0 0 0 3px #fff, 0 0 0 5px ${colorCodes[color].hex}`
                  : "0 1px 3px rgba(0,0,0,0.3)",
                transform: isSelected ? "scale(1.15)" : "scale(1)",
                transition: "transform 0.12s, box-shadow 0.12s",
                position: "relative",
                flexShrink: 0,
              }}
            >
              {isSelected && (
                <span style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  color: "#fff",
                  textShadow: "0 1px 2px rgba(0,0,0,0.6)",
                  fontWeight: 900,
                }}>
                  ✓
                </span>
              )}
            </button>
          );
        })}
      </div>
      {/* Selected color label pill */}
      <div style={{
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: "20px",
        fontSize: "12px",
        fontWeight: 600,
        backgroundColor: colorCodes[selected]?.hex || "#ccc",
        color: colorCodes[selected]?.textColor || "#333",
        transition: "background-color 0.2s",
      }}>
        {colorCodes[selected]?.colorAr || "—"}
        {colorCodes[selected]?.value !== null && colorCodes[selected]?.value !== undefined
          ? ` (${colorCodes[selected].value})`
          : ""}
      </div>
    </div>
  );
}

function ResistorCalculator() {
  const [numBands, setNumBands] = useState(4);
  const [bandColors, setBandColors] = useState({
    first:      "brown",
    second:     "black",
    third:      "black",
    multiplier: "red",
    tolerance:  "gold",
    tempCoeff:  "brown",
  });
  const [result, setResult] = useState(null);

  const handleColorChange = (band, color) => {
    setBandColors((prev) => ({ ...prev, [band]: color }));
  };

  // Band definitions
  const bands = [
    { key: "first",      label: "لون الشريط الأول",   show: true },
    { key: "second",     label: "لون الشريط الثاني",  show: true },
    { key: "third",      label: "لون الشريط الثالث",  show: numBands >= 5 },
    { key: "multiplier", label: "لون المضاعف",        show: true },
    { key: "tolerance",  label: "لون التفاوت",        show: numBands >= 4 },
    { key: "tempCoeff",  label: "لون معامل الحرارة",  show: numBands === 6 },
  ];

  const digitColors     = Object.keys(colorCodes).filter((c) => colorCodes[c].value !== null);
  const toleranceColors = Object.keys(colorCodes).filter((c) => colorCodes[c].tolerance !== null);
  const tempCoeffColors = Object.keys(colorCodes).filter((c) => colorCodes[c].tempCoeff !== null);

  const getAllowedColors = (key) => {
    if (["first", "second", "third"].includes(key)) return digitColors;
    if (key === "tolerance") return toleranceColors;
    if (key === "tempCoeff") return tempCoeffColors;
    return null;
  };

  // Auto-calculate on every change
  useEffect(() => {
    const firstDigit  = colorCodes[bandColors.first]?.value;
    const secondDigit = colorCodes[bandColors.second]?.value;
    const thirdDigit  = numBands >= 5 ? colorCodes[bandColors.third]?.value : 0;
    const multiplier  = colorCodes[bandColors.multiplier]?.multiplier;
    const tolerance   = colorCodes[bandColors.tolerance]?.tolerance;
    const tempCoeff   = numBands === 6 ? colorCodes[bandColors.tempCoeff]?.tempCoeff : null;

    let resistance = null;
    if (
      firstDigit  !== null && firstDigit  !== undefined &&
      secondDigit !== null && secondDigit !== undefined &&
      multiplier  !== null && multiplier  !== undefined
    ) {
      if (numBands === 3 || numBands === 4) {
        resistance = (firstDigit * 10 + secondDigit) * multiplier;
      } else {
        resistance = (firstDigit * 100 + secondDigit * 10 + thirdDigit) * multiplier;
      }
    }

    const formatted = formatResistance(resistance);
    const tolerancePct = parseFloat(tolerance);
    let minR = null, maxR = null;
    if (resistance !== null && !isNaN(tolerancePct)) {
      minR = formatResistance(resistance * (1 - tolerancePct / 100));
      maxR = formatResistance(resistance * (1 + tolerancePct / 100));
    }

    setResult({ formatted, tolerance, tempCoeff, minR, maxR });
  }, [bandColors, numBands]);

  return (
    <div className="rtl">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />

      <style>{`
        .rc-result-card {
          background: linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%);
          border-radius: 12px;
          padding: 24px;
          color: #fff;
          margin-top: 16px;
        }
        .rc-result-value {
          font-size: 42px;
          font-weight: 700;
          letter-spacing: -1px;
          line-height: 1;
        }
        .rc-result-unit {
          font-size: 22px;
          font-weight: 400;
          opacity: 0.85;
          margin-right: 4px;
        }
        .rc-result-row {
          display: flex;
          gap: 12px;
          margin-top: 14px;
          flex-wrap: wrap;
        }
        .rc-result-badge {
          background: rgba(255,255,255,0.15);
          border-radius: 8px;
          padding: 8px 14px;
          font-size: 13px;
          line-height: 1.4;
        }
        .rc-result-badge b {
          display: block;
          opacity: 0.75;
          font-size: 11px;
          margin-bottom: 2px;
        }
        .rc-band-chip {
          width: 26px;
          height: 26px;
          border-radius: 6px;
          border: 2px solid rgba(255,255,255,0.4);
          display: inline-block;
          transition: background-color 0.2s;
        }
        .rc-bands-btn {
          padding: 8px 18px;
          border-radius: 8px;
          border: 2px solid #e0e0e0;
          background: #fff;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          color: #555;
          transition: all 0.15s;
        }
        .rc-bands-btn.active {
          border-color: #2e7d32;
          background: #2e7d32;
          color: #fff;
        }
        .rc-bands-btn:hover:not(.active) {
          border-color: #2e7d32;
          color: #2e7d32;
        }
        .rc-svg-wrapper {
          background: #f5f5f0;
          border-radius: 12px;
          padding: 20px 16px 12px;
          margin-bottom: 8px;
          text-align: center;
        }
        .rc-svg-label {
          font-size: 11px;
          color: #999;
          margin-top: 6px;
          text-align: center;
        }
      `}</style>

      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ height: "50px" }}>
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>

          <div className="border border-light">
            {/* ===== ORIGINAL INTRO TEXT ===== */}
            <div className="row">
              <div>
                <p>
                  حاسبة المقاومة المتقدمة هذه تساعدك في حساب قيمة المقاومة بدقة
                  بناءً على الألوان المحددة على المقاومات. قم باستخدام هذه
                  الأداة الشاملة لتحديد قيمة المقاومة بدقة، مع إمكانية حساب
                  مقاومة مجموعة من المقاومات المتصلة على التوالي أو بالتوازي،
                  وكذلك حساب مقاومة الموصلات بناءً على حجمها وموصليتها.
                </p>
                <ul>
                  <li>اختر عدد الأشرطة على المقاومة (مثل 4 أشرطة، 5 أشرطة، إلخ).</li>
                  <li>حدد اللون لكل شريط وفقًا للرموز اللونية القياسية للمقاومات.</li>
                  <li>ستظهر النتيجة تلقائياً بمجرد اختيار الألوان.</li>
                  <li>
                    ستظهر النتيجة في الأسفل، متضمنة تفاصيل إضافية مثل المقاومة
                    الإجمالية لمجموعة المقاومات إذا كنت تقوم بالحسابات لإعدادات
                    مختلفة.
                  </li>
                </ul>
                <p className="alert alert-info">
                  <b>كيفية قراءة أشرطة المقاومة:</b>
                  <br />
                  لتحديد قيمة المقاومة بشكل صحيح، يجب أن تبدأ دائمًا بالقراءة من الطرف الذي يحتوي على الأشرطة المتقاربة (التي تكون أقرب لبعضها البعض). الشريط الأول يكون عادةً أقرب إلى أحد أطراف المقاومة، بينما شريط التفاوت (عادةً الذهبي أو الفضي) يكون منفصلاً بمسافة أكبر عن باقي الأشرطة.
                  <br />
                  <b>الشريط الأول:</b> يكون الأقرب إلى أحد أطراف المقاومة.
                  <br />
                  <b>شريط التفاوت:</b> عادةً ما يكون ذهبيًا أو فضيًا ويفصل بمسافة أكبر عن الأشرطة الأخرى. هذا الشريط يدل على نهاية القراءة.
                </p>
                استفد من هذه الأداة للحصول على حسابات دقيقة لمشاريعك الإلكترونية
                وتحديد القيم المناسبة لمكونات الدائرة الكهربائية الخاصة بك.
                <hr />
              </div>
            </div>

            <MobileUnderCalAds />

            {/* ===== CALCULATOR ROW ===== */}
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>{TITLE}</h2>
                </div>
                <hr />

                <div dir="rtl">
                  {/* Band count — pill buttons */}
                  <div className="form-group">
                    <b><label >عدد الأشرطة</label></b>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "8px", marginBottom: "20px" }}>
                      {[3, 4, 5, 6].map((n) => (
                        <button
                          key={n}
                          className={`rc-bands-btn${numBands === n ? " active" : ""}`}
                          onClick={() => setNumBands(n)}
                        >
                          {n} أشرطة
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* ── LIVE SVG RESISTOR ── updates as you pick colors */}
                  <div className="rc-svg-wrapper">
                    <ResistorSVG numBands={numBands} bandColors={bandColors} bands={bands} />
                    <div className="rc-svg-label">معاينة مباشرة للمقاومة</div>
                  </div>

                  <br />

                  {/* Color swatch pickers */}
                  {bands.filter((b) => b.show).map(({ key, label }) => (
                    <SwatchPicker
                      key={key}
                      label={label}
                      bandKey={key}
                      selected={bandColors[key]}
                      onChange={handleColorChange}
                      allowedColors={getAllowedColors(key)}
                    />
                  ))}
                </div>

                <hr />

                {/* Result card */}
                {result && (
                  <div className="rc-result-card" dir="rtl">
                    <div style={{ fontSize: "12px", opacity: 0.7, marginBottom: "4px" }}>قيمة المقاومة</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
                      <span className="rc-result-value">{result.formatted.value}</span>
                      <span className="rc-result-unit">{result.formatted.unit}</span>
                    </div>
                    <div className="rc-result-row">
                      {result.tolerance && (
                        <div className="rc-result-badge">
                          <b>التفاوت</b>
                          {result.tolerance}
                        </div>
                      )}
                      {result.minR && result.maxR && (
                        <div className="rc-result-badge">
                          <b>نطاق القيمة</b>
                          {result.minR.value} {result.minR.unit} — {result.maxR.value} {result.maxR.unit}
                        </div>
                      )}
                      {numBands === 6 && result.tempCoeff && (
                        <div className="rc-result-badge">
                          <b>معامل الحرارة</b>
                          {result.tempCoeff}
                        </div>
                      )}
                    </div>
                    {/* Mini chip preview */}
                    <div style={{ display: "flex", gap: "6px", marginTop: "14px", alignItems: "center" }}>
                      {bands.filter((b) => b.show).map(({ key }) => (
                        <div
                          key={key}
                          className="rc-band-chip"
                          style={{ backgroundColor: colorCodes[bandColors[key]]?.hex || "#ccc" }}
                          title={colorCodes[bandColors[key]]?.colorAr}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* ── IMAGE COLUMN — original images, unchanged ── */}
              <div className="col">
                {numBands === 3 && (
                  <img
                    loading="lazy"
                    src={band_3}
                    alt="3-Band Resistor"
                    style={{
                      width: "100%",
                      maxHeight: isMobile ? "400px" : "500px",
                      objectFit: "contain",
                    }}
                  />
                )}
                {numBands === 4 && (
                  <img
                    loading="lazy"
                    src={band_4}
                    alt="4-Band Resistor"
                    style={{
                      width: "100%",
                      maxHeight: isMobile ? "300px" : "500px",
                      objectFit: "contain",
                    }}
                  />
                )}
                {numBands === 5 && (
                  <img
                    loading="lazy"
                    src={band_5}
                    alt="5-Band Resistor"
                    style={{
                      width: "100%",
                      maxHeight: isMobile ? "300px" : "500px",
                      objectFit: "contain",
                    }}
                  />
                )}
                {numBands === 6 && (
                  <img
                    loading="lazy"
                    src={band_6}
                    alt="6-Band Resistor"
                    style={{
                      width: "100%",
                      maxHeight: isMobile ? "300px" : "500px",
                      objectFit: "contain",
                    }}
                  />
                )}
              </div>
            </div>
            {/* ===== END CALCULATOR ROW ===== */}
          </div>
        </div>

        <div className="col">
          <SecondAds />
          <MainCalculatorsList />
        </div>

        <div className="row"><div className="col"><ParallelResistorCalculator /></div></div>
        <div><br /></div>
        <div className="row"><div className="col"><SeriesResistorCalculator /></div></div>
        <div><br /></div>
        <div className="row"><div className="col"><ResistanceCalculator /></div></div>
        <div><br /></div>
        <div className="row">
          <div className="col-12 col-md-8"><RelatedLinks /></div>
        </div>
      </div>

      {/* ===== ORIGINAL INFO SECTIONS — unchanged ===== */}
      <div className="col-12 col-md-8">
        <div className="info-sections-container">
          <div className="info-section">
            <div className="info-section-header">
              <h3 style={{ fontSize: "16px" }}>المقاومات على التوالي</h3>
            </div>
            <div className="info-section-body">
              <p>
                عندما يتم توصيل المقاومات على التوالي، تكون المقاومة المكافئة هي
                مجموع قيم المقاومات الفردية. بمعنى آخر، يمكنك حساب المقاومة
                الإجمالية بإضافة قيم المقاومات معًا. على سبيل المثال، إذا كان
                لديك مقاومتان بقيمة 10 أوم و15 أوم موصلتان على التوالي، فإن
                المقاومة المكافئة هي:
                <strong>10 أوم + 15 أوم = 25 أوم</strong>.
              </p>
              <p>
                مثال آخر: إذا كانت لديك ثلاث مقاومات بقيم 5 أوم، 10 أوم، و20 أوم
                على التوالي، فإن المقاومة المكافئة تكون:
                <strong>5 أوم + 10 أوم + 20 أوم = 35 أوم</strong>.
              </p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header">
              <h3 style={{ fontSize: "16px" }}>المقاومات على التوازي</h3>
            </div>
            <div className="info-section-body">
              <p>
                عندما يتم توصيل المقاومات على التوازي، يكون مقلوب المقاومة
                المكافئة مساوياً لمجموع مقلوبات قيم المقاومات الفردية. يمكنك
                حساب المقاومة المكافئة باستخدام الصيغة:
                <strong>1/R_total = 1/R1 + 1/R2 + ... + 1/Rn</strong>, حيث{" "}
                <strong>R_total</strong> هي المقاومة المكافئة، و{" "}
                <strong>R1, R2, ..., Rn</strong> هي المقاومات الفردية.
              </p>
              <p>
                على سبيل المثال، إذا كانت لديك مقاومتان بقيمة 4 أوم و6 أوم
                موصلتان على التوازي، فإن المقاومة المكافئة يمكن حسابها كالتالي:
                <strong>1/R_total = 1/4 + 1/6</strong>، وبالتالي{" "}
                <strong>R_total = 2.4 أوم</strong>.
              </p>
              <p>
                مثال آخر: إذا كانت لديك ثلاث مقاومات بقيم 2 أوم، 3 أوم، و6 أوم
                على التوازي، فإن المقاومة المكافئة هي:
                <strong>1/R_total = 1/2 + 1/3 + 1/6</strong>، وبالتالي{" "}
                <strong>R_total = 1 أوم</strong>.
              </p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header">
              <h3 style={{ fontSize: "16px" }}>حاسبة مقاومة الموصل</h3>
            </div>
            <div className="info-section-body">
              <p>
                حاسبة مقاومة الموصل تساعدك في حساب مقاومة الموصلات بناءً على
                حجمها وموصليتها. يمكن أن تكون مقاومة الموصلات مختلفة بناءً على
                نوع المادة المستخدمة وطول وقطر الموصل. لاستخدام هذه الحاسبة، قم
                بإدخال المعلومات التالية:
              </p>
              <ul>
                <li>طول الموصل</li>
                <li>قطر الموصل</li>
                <li>موصلية المادة</li>
              </ul>
              بعد إدخال هذه القيم، اضغط على زر "احسب المقاومة" للحصول على القيمة
              الدقيقة لمقاومة الموصل.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResistorCalculator;